import { useMutation } from '@tanstack/react-query';
import { splitTransactionInterest } from '@dealstack/frontend/app/services/Transactions.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { GetTransactionRes } from '@dealstack/shared/lib/generated';

interface Props {
	transaction: GetTransactionRes;
	refreshTransactionsList: () => void;
	onClose: () => void;
}

// CAP-2011 "Update interest transferred": confirm-only flow — the split is parameterless (the
// backend stamps the full consideration on the beneficial leg and 0 on the legal leg).
export const useSplitInterestDrawerController = ({
	transaction,
	refreshTransactionsList,
	onClose,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const splitInterestRequest = useMutation({
		mutationFn: splitTransactionInterest,
	});

	const onConfirm = () => {
		splitInterestRequest.mutate(
			{ id: transaction.id },
			{
				onSuccess: () => {
					showToastV2({
						state: 'success',
						message: tCapManage('transactions.split_interest.success', {
							defaultValue:
								'Transaction split into beneficial and legal interest legs',
						}),
					});
					onClose();
					refreshTransactionsList();
				},
				onError: () => {
					showToastV2({
						state: 'error',
						message: tCapManage('transactions.split_interest.error', {
							defaultValue: 'Failed to split the transaction interest',
						}),
					});
				},
			}
		);
	};

	return { onConfirm, splitInterestRequest };
};
